import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { Photo } from '@/lib/types';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, storage } from '@/lib/firebase';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { string } from 'zod';

export type PhotoTab = 'public' | 'my-photos' | 'my-uploads' | 'bride-groom';

async function fetchPublicPhotos(eventId: string) {
  const photos: Photo[] = [];
  let photosQuery = query(collection(db, 'events', eventId, 'photos'));
  const querySnapshot = await getDocs(photosQuery);
  querySnapshot.forEach((doc) => {
    const photoData = doc.data() as Photo;
    photoData.id = doc.id;
    photoData.eventId = eventId;
    photos.push(photoData);
  });
  return photos;
}

async function fetchBrideGroomPhotos(eventId: string, token: string) {
  const response = await fetch(`${process.env.NEXT_PUBLIC_CLOUD_FUNCTIONS_URL}/get_bride_groom_photos?eventId=${eventId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch bride-groom photos');
  }
  return await response.json();
}

async function fetchMyPhotos(eventId: string, token: string) {
  const response = await fetch(`${process.env.NEXT_PUBLIC_CLOUD_FUNCTIONS_URL}/get_my_photos?eventId=${eventId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch user photos');
  }
  return await response.json();
}

async function fetchMyUploadsFromStorageForEvent(userId: string, eventId: string): Promise<Photo[]> {
  const userEventUploadsRef = ref(storage, `events/${eventId}/users/${userId}/thumbnails`);

  try {
    const res = await listAll(userEventUploadsRef);
    const photoPromises = res.items.map(async (item) => {
      const url = await getDownloadURL(item);
      const photoUrl= url.replace("thumbnails","photo_uploads");
      const metadata = await getMetadata(item);
      return {
        id: item.fullPath,
        imageUrl: photoUrl,
        thumbnailUrl: url,
        uploadedByUserId: userId,
        eventId: eventId,
        createdAt: new Date(metadata.timeCreated) as any,
        originalFileName: metadata.customMetadata?.originalFileName || item.name,
      } as Photo;
    });

    return Promise.all(photoPromises);
  } catch (error) {
    console.error("Error fetching user uploads from storage:", error);
    return [];
  }
}

export function usePhotos(activeTab: PhotoTab, eventId: string) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    const getPhotos = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let fetchedPhotos: Photo[] = [];
        if (activeTab === 'public') {
          fetchedPhotos = await fetchPublicPhotos(eventId);
        } else if (user) {
          if (activeTab === 'my-photos') {
            const token = await user.getIdToken();
            fetchedPhotos = await fetchMyPhotos(eventId, token);
          } else if (activeTab === 'bride-groom') {
            const token = await user.getIdToken();
            fetchedPhotos = await fetchBrideGroomPhotos(eventId, token);
          } else if (activeTab === 'my-uploads') {
            fetchedPhotos = await fetchMyUploadsFromStorageForEvent(user.uid, eventId);
          }
        }
        setPhotos(fetchedPhotos);
      } catch (err: any) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (!authLoading) {
      getPhotos();
    }
  }, [activeTab, user, authLoading, eventId]);

  return { photos, isLoading, error };
}
